import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { saveExpense, fetchExpense, deleteExpense } from 'actions/expense';
import { getTravelDetail } from 'actions/travelRequested';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Expense as ExpenseModel, ExpenseForm } from 'models/expense';
import { TravelDetails } from 'models/travelDetails';
import Expense from 'screens/components/Expense';
import { extractErrorMessage, base64ToFile } from 'utils/generic';

const ExpenseContainer = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const history = useHistory<{ id: string }>();
  const { expenseId } = useParams<{ expenseId: string }>();
  const { t } = useStoreTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expense, setExpense] = useState<ExpenseModel>();
  const [travel, setTravel] = useState<TravelDetails>();
  const [isLoadingExpense, setIsLoadingExpense] = useState(false);
  const [isLoadingTravel, setIsLoadingTravel] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successTravelId, setSuccessTravelId] = useState<string | undefined>(history.location.state?.id);

  const travelId = history.location.state?.id;

  useEffect(() => {
    const fetch = async () => {
      if (!expenseId) return;

      try {
        setIsLoadingExpense(true);
        const {
          data: { data: expense },
        } = await fetchExpense(expenseId);

        if (expense.file && expense.fileExtension) {
          base64ToFile(('data:text/plain;base64,' + expense.file) as string, expense.fileExtension).then(
            (file: File) => {
              expense.file = file;
              setExpense(expense);
            },
          );
        } else {
          setExpense(expense);
        }
      } catch (ex) {
        toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
      } finally {
        setIsLoadingExpense(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId]);

  useEffect(() => {
    const fetch = async () => {
      if (!travelId && !expense?.travelId) return;

      try {
        setIsLoadingTravel(true);
        const {
          data: { data: travel },
        } = await getTravelDetail(travelId || expense?.travelId || '');
        setTravel(travel);
      } catch (ex) {
        toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
      } finally {
        setIsLoadingTravel(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelId, expense]);

  const handleSave = async (data: ExpenseForm) => {
    try {
      setIsSaving(true);
      await dispatch<any>(
        saveExpense(
          {
            ...data,
            travelId: travelId || expense?.travelId,
          },
          expense,
        ),
      );
      setShowSuccess(true);
      setSuccessTravelId(travelId || expense?.travelId);
    } catch (ex) {
      setIsSaving(false);
      toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
    }
  };

  const handleDelete = (expenseId: string) => {
    setIsDeleting(true);
    deleteExpense(expenseId)
      .then(() => {
        history.goBack();
        toast.add({ type: 'success', message: t('expense.delete.deleteExpenseSuccess') });
      })
      .catch((ex: any) => {
        setIsDeleting(false);
        toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
      });
  };

  return (
    <Expense
      saving={isSaving}
      loading={isLoadingExpense || isLoadingTravel || isDeleting}
      travel={travel}
      expense={expense}
      onSave={handleSave}
      onDelete={handleDelete}
      showSuccess={showSuccess}
      successTravelId={successTravelId}
    />
  );
};

export default ExpenseContainer;
